import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { NzCalendarMode } from 'ng-zorro-antd/calendar';
import { FormationSearchParams } from '@app/models/formation';
import {
	FormationThematiqueSearchParams,
	FormationThematique,
	FormationThematiqueService,
} from '@app/models/formation-thematique';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ErrorService } from '@app/services/error.service';
import { Color } from '@app/models/color';
import { User } from '@app/models/user';

registerLocaleData(en);

@Component({
	selector: 'app-atom-base-calendar',
	template: '',
})
export abstract class BaseCalendarComponent implements OnInit, OnDestroy {
	date: Date;
	date_min: Date;
	date_max: Date;
	mode: NzCalendarMode = 'month';

	searchParams: FormationSearchParams = new FormationSearchParams({
		_page: 0,
		_limit: 100,
		_order: 'asc',
		_sort: null,
	});
	sorts = ['created_at', 'name'];

	listDateMap: {
		[key: number]: {
			id: string;
			title: string;
			color: Color;
			month: number;
		}[];
	};

	@Input() user: User;

	/** @type {Subject<void>} Observables unsubscriber */
	protected unsubscribe: Subject<void> = new Subject<void>();

	/** Search params of model */
	@Input() formationThematiqueSearchParams: FormationThematiqueSearchParams;

	items: FormationThematique[] = [];
	loading = true;

	constructor(
		private formationThematiqueService: FormationThematiqueService,
		private errorService: ErrorService
	) {}

	async ngOnInit(): Promise<void> {
		this.date = new Date();
		this.changeFiltre(this.date);
		this.formationThematiqueSearchParams
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.refresh();
			});
	}

	ngOnDestroy() {
		this.unsubscribe.next();
	}

	//Event call after each filter change of calendar
	abstract changeFiltre(date: Date): void;

	async refresh() {
		this.loading = true;
		if (this.formationThematiqueSearchParams) {
			const baseParams = {
				...this.formationThematiqueSearchParams.toObject(),
				start_date__min: this.date_min,
				start_date__max: this.date_max,
			};

			// If a specific formation is selected via the dropdown, filter by it
			if (
				this.searchParams.props._id &&
				this.searchParams.props._id.length
			) {
				baseParams['formation'] = this.searchParams.props._id;
			}

			try {
				// Step 1: Fetch first page
				const params = new FormationThematiqueSearchParams(
					baseParams
				).toObject();
				const firstPage = await this.formationThematiqueService.list(
					params
				);
				let allItems = firstPage.items;

				// Step 2: If more pages exist, fetch them all in parallel
				const totalPages = Math.ceil(firstPage.total / firstPage.limit);
				if (totalPages > 1) {
					const promises = [];
					for (let page = 1; page < totalPages; page++) {
						const pageParams = new FormationThematiqueSearchParams({
							...baseParams,
							_page: page,
						}).toObject();
						promises.push(
							this.formationThematiqueService.list(pageParams)
						);
					}
					const results = await Promise.all(promises);
					for (const result of results) {
						allItems = allItems.concat(result.items);
					}
				}

				this.calendarFormat(allItems, this.mode);
			} catch (err) {
				this.errorService.handle(err);
			}
		}

		this.loading = false;
	}

	abstract calendarFormat(items, mode): void;
}
